import { useQuery } from '@apollo/client';
import useDialog from 'hooks/useDialog';
import {
  GET_QUESTIONNAIRE_OVERVIEW,
  GET_QUESTIONNAIRE_COMPANY_RESPONSE,
  GET_QUESTIONNAIRE_COMPANY_RESPONSES,
} from 'graphql/secure/queries/questionnaire.queries';
import {
  IQuestionnaireResponseConnection,
  IQuestionnaireResponsesFilter,
  IQuestionnaireResponsesOrderBy,
} from 'graphql/secure/queries/types/questionnaire-query.types';
import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { EPartnerRequestStatus, IPartnerRequest } from 'FeatureModules/Partner/types/partner.types';
import {
  IQuestionnaire,
  IQuestionnaireCompanyResponse,
} from 'FeatureModules/Questionnaire/types/questionnaire.types';

const useQuestionnaireResponses = () => {
  const { questionnaireId, questionnaireResponseId } = useParams<{
    questionnaireId: string;
    questionnaireResponseId?: string;
  }>();
  const navigate = useNavigate();
  const { openDialog } = useDialog();
  const { data, loading, error } = useQuery<{
    questionnaire: IQuestionnaire;
  }>(GET_QUESTIONNAIRE_OVERVIEW, {
    variables: { id: questionnaireId },
    skip: !questionnaireId,
  });

  const {
    data: questionnaireResponseData,
    loading: questionnaireResponseLoading,
    error: questionnaireResponseError,
  } = useQuery<{
    questionnaireCompanyResponse: IQuestionnaireCompanyResponse;
  }>(GET_QUESTIONNAIRE_COMPANY_RESPONSE, {
    variables: {
      id: questionnaireResponseId,
    },
    skip: !questionnaireResponseId,
    fetchPolicy: 'no-cache',
  });

  const {
    data: questionnaireResponsesData,
    loading: questionnaireResponsesLoading,
    error: questionnaireResponsesError,
  } = useQuery<
    {
      questionnaireCompanyResponses: IQuestionnaireResponseConnection;
    },
    {
      questionnaireId: string;
      filters: IQuestionnaireResponsesFilter;
      orderBy: IQuestionnaireResponsesOrderBy;
    }
  >(GET_QUESTIONNAIRE_COMPANY_RESPONSES, {
    variables: {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      questionnaireId: questionnaireId!,
      filters: {
        requestStatus: [EPartnerRequestStatus.PROVIDED, EPartnerRequestStatus.PROVIDED_COMPLETED],
        requestedToCompanyId: undefined,
      },
      orderBy: { orderByMethod: 'ASC', orderByField: 'riskLevel' },
    },
    skip: !questionnaireId,
  });

  const questionnaire = data?.questionnaire;
  const questionnaireResponse = questionnaireResponseData?.questionnaireCompanyResponse;

  const providedQuestionnairesRequests =
    questionnaireResponsesData?.questionnaireCompanyResponses.edges
      .filter(
        response =>
          response.node.partnerRequest &&
          (response.node.partnerRequest.requestStatus === EPartnerRequestStatus.PROVIDED ||
            response.node.partnerRequest.requestStatus === EPartnerRequestStatus.PROVIDED_COMPLETED)
      )
      // Can not be null due to the filter above
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      .map<IPartnerRequest>(response => response.node.partnerRequest!) || [];

  const selectedQuestionnaireResponseIndex =
    (providedQuestionnairesRequests?.findIndex(
      ({ questionnaireCompanyResponseId: companyQuestionnaireResponseId }) =>
        companyQuestionnaireResponseId === questionnaireResponseId
    ) || 0) + 1;

  /**
   * Automatically select the first provided questionnaire response if none is selected
   */
  useEffect(() => {
    if (
      !questionnaireResponseId &&
      questionnaire &&
      providedQuestionnairesRequests?.[0]?.questionnaireCompanyResponseId
    ) {
      navigate(providedQuestionnairesRequests?.[0]?.questionnaireCompanyResponseId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionnaireId, questionnaireResponseId, providedQuestionnairesRequests]);

  const handleSelectResponse = (selectedQuestionnaireResponseId: string) => {
    navigate(`/questionnaires/${questionnaireId}/responses/${selectedQuestionnaireResponseId}`);
  };

  const handleSelectPreviousResponse = () => {
    if (selectedQuestionnaireResponseIndex > 1 && providedQuestionnairesRequests) {
      const previousResponseId =
        providedQuestionnairesRequests[selectedQuestionnaireResponseIndex - 2]
          .questionnaireCompanyResponseId;
      navigate(`/questionnaires/${questionnaireId}/responses/${previousResponseId}`);
    }
  };

  const handleSelectNextResponse = () => {
    if (
      selectedQuestionnaireResponseIndex < (providedQuestionnairesRequests?.length || 0) &&
      providedQuestionnairesRequests
    ) {
      const nextResponseId =
        providedQuestionnairesRequests[selectedQuestionnaireResponseIndex]
          .questionnaireCompanyResponseId;
      navigate(`/questionnaires/${questionnaireId}/responses/${nextResponseId}`);
    }
  };

  const handleAcceptRequest = (request: IPartnerRequest) => {
    openDialog({
      type: 'REVIEW_QUESTIONNAIRE_REQUEST',
      props: { request },
    });
  };

  return {
    selectedQuestionnaireResponseIndex,
    loading: loading || questionnaireResponseLoading || questionnaireResponsesLoading,
    error: error || questionnaireResponseError || questionnaireResponsesError,
    questionnaire,
    providedQuestionnairesRequests,
    questionnaireResponse,
    handleSelectResponse,
    handleSelectPreviousResponse,
    handleSelectNextResponse,
    handleAcceptRequest,
  };
};

export default useQuestionnaireResponses;
